import { RefObject, useCallback, useEffect, useState } from 'react'

interface PagingState {
    page: number
    pageCount: number
}

/**
 * Where each page of the scroller starts (scrollLeft). A page starts at the first child that the previous page showed
 * cut off, so no child is skipped and every page starts at the edge of a child; the last page ends at the end of the content.
 */
const getPageStarts = (scroller: HTMLElement): number[] => {
    const maxScroll = scroller.scrollWidth - scroller.clientWidth
    if (maxScroll <= 1) return [0]

    const scrollerLeft = scroller.getBoundingClientRect().left - scroller.scrollLeft
    const children = Array.from(scroller.children).map(child => {
        const rect = child.getBoundingClientRect()
        return { left: rect.left - scrollerLeft, right: rect.right - scrollerLeft }
    })

    const starts = [0]
    for (;;) {
        const viewEnd = starts[starts.length - 1] + scroller.clientWidth
        const next = children.find(child => child.right > viewEnd + 1 && child.left > starts[starts.length - 1] + 1)
        if (!next || next.left >= maxScroll - 1) break
        starts.push(next.left)
    }

    return [...starts, maxScroll]
}

/**
 * Pages for a horizontal scroller whose children have different widths. (`useHorizontalPaging` is for cards of one width.)
 * `itemCount` only makes the hook measure again when the content changes.
 */
const useScrollPaging = (scrollerRef: RefObject<HTMLElement | null>, itemCount: number) => {
    const [state, setState] = useState<PagingState>({ page: 0, pageCount: 1 })

    const measure = useCallback(() => {
        const scroller = scrollerRef.current
        if (!scroller || !scroller.clientWidth) return

        const starts = getPageStarts(scroller)
        const pageCount = starts.length
        const atEnd = scroller.scrollLeft >= scroller.scrollWidth - scroller.clientWidth - 1
        let page = 0
        starts.forEach((start, index) => {
            if (scroller.scrollLeft >= start - 1) page = index
        })

        setState(prev => {
            const next = { page: atEnd ? pageCount - 1 : page, pageCount }
            return prev.page === next.page && prev.pageCount === next.pageCount ? prev : next
        })
    }, [scrollerRef])

    useEffect(() => {
        const scroller = scrollerRef.current
        if (!scroller) return

        measure()
        scroller.addEventListener('scroll', measure, { passive: true })
        const observer = typeof ResizeObserver === 'undefined' ? undefined : new ResizeObserver(measure)
        observer?.observe(scroller)

        return () => {
            scroller.removeEventListener('scroll', measure)
            observer?.disconnect()
        }
    }, [scrollerRef, measure, itemCount])

    const goToPage = useCallback((target: number) => {
        const scroller = scrollerRef.current
        if (!scroller) return

        const starts = getPageStarts(scroller)
        const left = starts[Math.max(0, Math.min(starts.length - 1, target))]
        scroller.scrollTo?.({ left, behavior: 'smooth' })
    }, [scrollerRef])

    return { page: state.page, pageCount: state.pageCount, goToPage }
}

export default useScrollPaging
